/**
 * Application-wide constants
 * Centralizes magic numbers, strings, and configuration values
 */

// Timing Constants (in milliseconds)
export const TIMING = {
  DEFAULT_TIMEOUT: 2000,
  API_TIMEOUT: 30000,
  DEBOUNCE_DELAY: 300,
  ANIMATION_DURATION: 300,
  SESSION_CHECK_INTERVAL: 60000, // 1 minute
  PERFORMANCE_LOG_INTERVAL: 30000 // 30 seconds
};

// API Configuration
export const API_CONFIG = {
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000,
  MAX_FILE_SIZE: 10 * 1024 * 1024 // 10MB
};

// UI Constants
export const UI = {
  MAX_TABLE_ROWS: 50,
  ITEMS_PER_PAGE: 10,
  MIN_PASSWORD_LENGTH: 6,
  MAX_INPUT_LENGTH: 255,
  TOAST_DURATION: 4000
};

// Status Constants
export const STATUS = {
  IDLE: 0,
  PROCESSING: 1,
  COMPLETED: 2,
  ERROR: -1
};

// File Types
export const ACCEPTED_FILE_TYPES = {
  CV: {
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'text/plain': ['.txt']
  },
  IMAGE: {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif']
  }
};

// Role Levels (for comparison)
export const ROLE_LEVELS = {
  USER: 1,
  MANAGER: 2,
  ADMIN: 3
};

// Competency Categories
export const COMPETENCY_CATEGORIES = [
  'Competences techniques',
  'Competences comportementales',
  'Leadership',
  'Connaissance du domaine',
  'Outils',
  'Methodologies',
  'Certifications',
  'Other'
];

// Proficiency Levels
export const PROFICIENCY_LEVELS = [
  'Debutant',
  'Intermediaire',
  'Avance',
  'Expert'
];

// Error Types
export const ERROR_TYPES = {
  NETWORK: 'NETWORK_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  AUTHENTICATION: 'AUTH_ERROR',
  AUTHORIZATION: 'AUTHORIZATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  SERVER: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR'
};

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'autobot_session_token',
  USER_LANGUAGE: 'autobot_language',
  ADMIN_SESSION: 'autobot_admin_session',
  USER_PREFERENCES: 'autobot_user_preferences',
  DRAFT_DATA: 'autobot_draft_data'
};

// Regular Expressions
export const REGEX = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^\+?[\d\s-()]+$/,
  URL: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/,
  ALPHANUMERIC: /^[a-zA-Z0-9]+$/
};

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM DD, YYYY',
  API: 'YYYY-MM-DD',
  DATETIME: 'YYYY-MM-DD HH:mm:ss',
  TIME_ONLY: 'HH:mm'
};

// Theme Colors (if not using MUI theme)
export const COLORS = {
  PRIMARY: '#1976d2',
  SECONDARY: '#dc004e',
  SUCCESS: '#4caf50',
  WARNING: '#ff9800',
  ERROR: '#f44336',
  INFO: '#2196f3'
};

// Breakpoints (matching MUI defaults)
export const BREAKPOINTS = {
  XS: 0,
  SM: 600,
  MD: 960,
  LG: 1280,
  XL: 1920
};

// Animation Easing
export const EASING = {
  STANDARD: 'cubic-bezier(0.4, 0, 0.2, 1)',
  DECELERATE: 'cubic-bezier(0, 0, 0.2, 1)',
  ACCELERATE: 'cubic-bezier(0.4, 0, 1, 1)',
  SHARP: 'cubic-bezier(0.4, 0, 0.6, 1)'
};

// Version Control
export const VERSION = {
  MAX_VERSIONS: 10,
  AUTO_SAVE_INTERVAL: 300000 // 5 minutes
};

// Validation Rules
export const VALIDATION = {
  MIN_NAME_LENGTH: 2,
  MAX_NAME_LENGTH: 50,
  MIN_DESCRIPTION_LENGTH: 10,
  MAX_DESCRIPTION_LENGTH: 500,
  ALLOWED_IMAGE_SIZE: 5 * 1024 * 1024 // 5MB
};

// Export all constants as default for easy access
const constants = {
  TIMING,
  API_CONFIG,
  UI,
  STATUS,
  ACCEPTED_FILE_TYPES,
  ROLE_LEVELS,
  COMPETENCY_CATEGORIES,
  PROFICIENCY_LEVELS,
  ERROR_TYPES,
  STORAGE_KEYS,
  REGEX,
  DATE_FORMATS,
  COLORS,
  BREAKPOINTS,
  EASING,
  VERSION,
  VALIDATION
};

export default constants;
